import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useScoreHistory } from '../hooks/useScoreHistory';

interface Props {
  userId: string;
  fertilityScore?: number | null;
}

export function ProgressChart({ userId, fertilityScore }: Props) {
  const { t, i18n } = useTranslation();
  const { data, loading } = useScoreHistory(userId, fertilityScore);

  const chartData = data.map((entry) => {
    const date = new Date(entry.day + 'T00:00:00');
    const label = new Intl.DateTimeFormat(i18n.language, { weekday: 'short' }).format(date);
    return {
      day: label.charAt(0).toUpperCase() + label.slice(1),
      score: entry.score,
    };
  });

  // Dynamic Y-axis domain
  const scores = chartData.map(d => d.score);
  const minScore = scores.length > 0 ? Math.min(...scores) : 0;
  const maxScore = scores.length > 0 ? Math.max(...scores) : 100;
  const yMin = Math.max(0, minScore - 10);
  const yMax = Math.min(100, maxScore + 10);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
      className="w-full h-[300px] bg-white rounded-[2.5rem] p-6 shadow-[0_8px_40px_rgba(0,0,0,0.04)] border border-stone-100"
    >
      <div className="flex justify-between items-center mb-6 px-2">
        <div>
          <h3 className="text-lg font-bold text-stone-900 tracking-tight">{t('progress.title')}</h3>
          <p className="text-xs font-semibold text-stone-400 uppercase tracking-widest">{t('progress.subtitle')}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <span className="text-[10px] font-bold text-stone-500 uppercase">{t('progress.actual')}</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-[70%]">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin" />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="70%">
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#a8a29e', fontSize: 11, fontWeight: 600 }}
              dy={10}
            />
            <YAxis
              hide
              domain={[yMin, yMax]}
            />
            <Tooltip
              contentStyle={{
                borderRadius: '1rem',
                border: 'none',
                boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
                fontSize: '12px',
                fontWeight: 'bold'
              }}
            />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#10b981"
              strokeWidth={4}
              dot={{ r: 4, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6, strokeWidth: 0 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </motion.div>
  );
}
